import {
  Component,
  Input,
  OnChanges,
  ViewChild,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  ElementRef,
  AfterViewInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule, MatSort, Sort } from '@angular/material/sort';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { SegmentDetailsModalComponent } from '../segment-details-modal/segment-details-modal.component';
import { DeleteSegmentModalComponent } from '../delete-segment-modal/delete-segment-modal.component';
import { ApiService } from '../../services/api.service';
import { CampaignSortUtils } from '../shared/campaign-sort.utils';
import { MobileDetectionMixin } from '../../shared/mixins/mobile-detection.mixin';
import { formatDateMonthDayYear } from '../../shared/utils/date.util';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { Segment } from '../../shared/types/segment.interface';
import { TableUtils, DragScrollConfig } from '../../orders/utils/table.utils';
import { EmptyStateComponent } from '../../shared/components/empty-state';

/**
 * Displays and manages a paginated, sortable and filterable table of user segments, providing actions to view details, edit, duplicate and delete segments.
 * @component
 * @selector app-segments-table
 * @example
 *   <app-segments-table [segments]="segments" [loading]="isLoading" (edit)="onEdit($event)"></app-segments-table>
 * @input Segment[] segments - The list of segments provided by the parent. The component performs client-side filtering and sorting on this list.
 * @input boolean loading - Whether the table should render in a loading/skeleton state.
 * @input any totalRecords - Total number of records used to compute pagination end item (can be numeric or server-provided format).
 * @input number originalSegmentCount - The original total count of segments (used to decide empty-state behavior and whether to show an add button).
 * @input boolean hasLoadedInitialSegments - Flag indicating if the parent has completed the initial segments load.
 * @output EventEmitter<any> edit - Emitted when a segment is requested to be edited. Payload: the selected segment object.
 * @output EventEmitter<any> delete - Emitted after a segment is successfully deleted. Payload: the deleted segment object.
 * @output EventEmitter<any> duplicate - Emitted when a segment is requested to be duplicated. Payload: the selected segment object.
 * @output EventEmitter<any> pageChange - Emitted when the paginator page changes. Payload: { page, rows }.
 * @output EventEmitter<any> rowsPerPageChange - Emitted when rows-per-page selection changes. Payload: { value }.
 * @output EventEmitter<void> addSegment - Emitted when the user requests to add/create a new segment.
 *
 * Key methods:
 * - ngOnInit(): component initialization (does not load segments; parent is responsible for that).
 * - ngOnChanges(): responds to changes of the @Input() segments, clones to allSegments and reapplies filters.
 * - applyFilters(): central client-side filtering that applies status filters and searchTerm and updates filteredSegments.
 * - onSearchChange(term): updates searchTerm, resets page and reapplies filters.
 * - toggleStatusFilter(), applyStatusFilter(), clearStatusFilter(): control the status filter overlay and selections.
 * - setCurrentSegment(segment) / showActionMenu(event, segment): set the active row and open the action menu.
 * - onViewSegment(), onViewSegmentDetails(segment), onCloseSegmentDetailsModal(): open/close the segment details modal.
 * - onEditSegment(), onDuplicateSegment(): emit edit/duplicate events for the current segment.
 * - onDeleteSegment(), onConfirmDelete(segment), onCloseDeleteModal(): prepare, call API to delete a segment, show success/error toasts, and emit delete on success.
 * - onPageChange(event), onRowsPerPageChange(event), getEndItem(): pagination handlers and helpers.
 * - customSort(event), getSortIcon(field): delegate custom sorting logic to shared sort utilities.
 * - getStatusDisplay(status), formatCreatedDate(dateString): small format helpers for UI display.
 *
 * Important state and behavior:
 * - Maintains internal allSegments and filteredSegments for client-side filter/sort.
 * - Tracks pagination state (currentPage, rowsPerPage), selectedStatuses, searchTerm, and custom sort state (currentSortField, currentSortOrder).
 * - Manages modals and action panels (showSegmentDetailsModal, showDeleteModal, actionMenuPanel) and deletion state (isDeletingSegment).
 * - Provides an emptyStateConfig getter to choose appropriate empty-state messaging and whether to show an "add" button based on originalSegmentCount and current filters/search.
 */
@Component({
  selector: 'app-segments-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    FormsModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatTooltipModule,
    MatMenuModule,
    MatCheckboxModule,
    MatSelectModule,
    SegmentDetailsModalComponent,
    DeleteSegmentModalComponent,
    EmptyStateComponent,
  ],
  templateUrl: './segments-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./segments-table.component.scss'],
})
export class SegmentsTableComponent
  extends MobileDetectionMixin
  implements OnChanges, AfterViewInit
{
  @ViewChild(MatSort) sort!: MatSort;

  private _tableScrollWrapper?: ElementRef<HTMLElement>;
  @ViewChild('tableScrollWrapper') set tableScrollWrapper(element: ElementRef<HTMLElement>) {
    if (element) {
      this._tableScrollWrapper = element;
      this.initializeDragScrolling();
    }
  }

  private dragScrollConfig!: DragScrollConfig;

  @Output() edit = new EventEmitter<any>();
  @Output() delete = new EventEmitter<any>();
  @Output() duplicate = new EventEmitter<any>();
  @Output() pageChange = new EventEmitter<any>();
  @Output() rowsPerPageChange = new EventEmitter<any>();
  @Output() addSegment = new EventEmitter<void>();

  @Input() segments: Segment[] = [];
  @Input() loading = false;
  @Input() totalRecords: any = 0;
  @Input() originalSegmentCount = 0;
  @Input() hasLoadedInitialSegments = false;

  // Client-side filtered segments
  filteredSegments: Segment[] = [];
  allSegments: Segment[] = [];
  // Number of rows shown in table (and skeleton)
  rows = 10;
  // Skeleton placeholder rows
  get skeletonRows(): { __skeleton: true; id: number }[] {
    return Array.from({ length: this.rowsPerPage }, (_, i) => ({ __skeleton: true, id: i }));
  }

  // Pagination properties
  currentPage = 0;
  rowsPerPage = 10;
  rowsPerPageOptions = [
    { label: '10', value: 10 },
    { label: '25', value: 25 },
    { label: '50', value: 50 },
  ];

  // Filter options
  statusOptions: any[] = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
  ];

  // Filter selections
  selectedStatuses: string[] = [];
  /** Working copy while the status filter panel is open; committed on Apply. */
  draftSelectedStatuses: string[] = [];
  searchTerm = '';

  // Filter panel visibility states
  isStatusFilterOpen = false;

  // Custom sort properties
  currentSortField = '';
  currentSortOrder: 'asc' | 'desc' | '' = '';

  // Material table properties
  dataSource = new MatTableDataSource<Segment>();
  displayedColumns: string[] = [
    'name',
    'status',
    'source_type',
    'created_at',
    'total_customers',
    'active_customers',
    'inactive_customers',
    'unsubscribed_customers',
    'actions',
  ];

  currentSegment: any = null;

  // Modal state
  showSegmentDetailsModal = false;
  selectedSegment: Segment | null = null;
  showDeleteModal = false;
  segmentToDelete: Segment | null = null;
  isDeletingSegment = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private apiService: ApiService,
    private messageService: NotificationService
  ) {
    super();
  }

  private initializeDragScrolling(): void {
    if (!this._tableScrollWrapper?.nativeElement) return;

    // Cleanup and re-initialize using TableUtils
    const config = TableUtils.reinitializeDragScrolling(this._tableScrollWrapper);
    if (config) {
      this.dragScrollConfig = config;
    }
  }

  ngAfterViewInit(): void {
    this.initializeDragScrolling();
  }

  /** Bridge Material's sort event onto the shared campaign sort helper. */
  onMatSortChange(event: Sort): void {
    const order = event.direction === 'asc' ? 1 : event.direction === 'desc' ? -1 : 0;
    this.customSort({ field: event.active, order, data: this.filteredSegments });
    this.dataSource.data = [...this.filteredSegments];
  }

  ngOnChanges(): void {
    // Update filtered segments when input segments change
    if (this.segments) {
      this.allSegments = [...this.segments];
      this.applyFilters();
      this.dataSource.data = this.filteredSegments;
    } else {
      // Ensure filteredSegments is cleared if segments is empty
      this.filteredSegments = [];
      this.allSegments = [];
      this.dataSource.data = [];
    }
  }

  setCurrentSegment(segment: any): void {
    this.currentSegment = segment;
  }

  onEditSegment(): void {
    if (this.currentSegment) {
      this.edit.emit(this.currentSegment);
    }
  }

  onDuplicateSegment(): void {
    if (this.currentSegment) {
      this.duplicate.emit(this.currentSegment);
    }
  }

  onDeleteSegment(): void {
    if (this.currentSegment) {
      this.segmentToDelete = this.currentSegment;
      this.showDeleteModal = true;
    }
  }

  /**
   * Confirm and delete a user segment via API, update UI state, show toasts, and emit a delete event.
   * @example
   * nConfirmDelete(sampleSegment)
   * undefined
   * @param {{Segment}} {{segment}} - Segment object to delete (should contain an id).
   * @returns {{void}} No return value; performs side effects such as API call, UI updates, toasts, and event emission.
   **/
  onConfirmDelete(segment: Segment): void {
    if (segment && segment.id) {
      this.isDeletingSegment = true;
      this.apiService.deleteSegment(segment.id).subscribe({
        next: response => {
          this.isDeletingSegment = false;
          if (response.status) {
            // Show success toast
            this.messageService.add({
              severity: 'success',
              summary: 'User segment deleted',
              detail: '“' + segment.name + '” has been removed successfully',
            });
            // Emit delete event to parent component to refresh the segments list
            this.showDeleteModal = false;
            this.segmentToDelete = null;
            // Close segment details modal after successful deletion
            this.showSegmentDetailsModal = false;
            this.selectedSegment = null;
            this.delete.emit(segment);
          } else {
            console.error('Failed to delete segment:', response.message);
            // Show error toast
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: response.message || 'Failed to delete segment',
            });
            this.showDeleteModal = false;
          }
        },
        error: error => {
          this.isDeletingSegment = false;
          console.error('Error deleting segment:', error);
          // Extract error message from error response
          const errorMessage =
            error?.error?.message ||
            error?.message ||
            'Failed to delete segment. Please try again.';
          // Show error toast with API error message
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
          });
          this.showDeleteModal = false;
        },
      });
    }
  }

  onCloseDeleteModal(): void {
    this.showDeleteModal = false;
    this.segmentToDelete = null;
    this.isDeletingSegment = false;
  }

  // Action menu methods
  onViewSegment(): void {
    if (this.currentSegment) {
      this.selectedSegment = this.currentSegment;
      this.showSegmentDetailsModal = true;
    }
  }

  onViewSegmentDetails(segment: any): void {
    this.setCurrentSegment(segment);
    this.selectedSegment = segment;
    this.showSegmentDetailsModal = true;
  }

  onCloseSegmentDetailsModal(): void {
    this.showSegmentDetailsModal = false;
    this.selectedSegment = null;
  }

  showActionMenu(event: Event, segment: any): void {
    event.preventDefault();
    event.stopPropagation();
    this.setCurrentSegment(segment);
    // Action menu is now handled via Material menu in template
  }

  // Filter methods
  toggleStatusFilter(event: Event) {
    if (!this.isStatusFilterOpen) {
      this.draftSelectedStatuses = [...this.selectedStatuses];
    }
    this.isStatusFilterOpen = !this.isStatusFilterOpen;
  }

  isStatusSelected(value: string): boolean {
    return this.draftSelectedStatuses.includes(value);
  }

  onStatusToggle(value: string, checked: boolean): void {
    if (checked) {
      if (!this.draftSelectedStatuses.includes(value)) {
        this.draftSelectedStatuses = [...this.draftSelectedStatuses, value];
      }
    } else {
      this.draftSelectedStatuses = this.draftSelectedStatuses.filter(status => status !== value);
    }
  }

  applyStatusFilter() {
    this.selectedStatuses = [...this.draftSelectedStatuses];
    this.isStatusFilterOpen = false;

    this.currentPage = 0; // Reset to first page when applying filters
    this.applyFilters();
  }

  clearStatusFilter() {
    this.selectedStatuses = [];
    this.draftSelectedStatuses = [];
    this.isStatusFilterOpen = false;

    this.currentPage = 0; // Reset to first page when clearing filters
    this.applyFilters();
  }

  cancelStatusFilter() {
    this.isStatusFilterOpen = false;
  }

  onSearchChange(searchTerm: string) {
    this.searchTerm = searchTerm;
    this.currentPage = 0; // Reset to first page when searching
    this.applyFilters();
  }

  // Main filtering method
  /**
   * Apply status and text search filters to this.allSegments, update this.filteredSegments, and trigger change detection.
   * @example
   * pplyFilters()
   * undefined
   * @param {void} none - No parameters.
   * @returns {void} Updates component state (this.filteredSegments) and calls this.cdr.detectChanges().
   */
  applyFilters() {
    let filtered = [...this.allSegments];

    // Apply status filter
    if (this.selectedStatuses && this.selectedStatuses.length > 0) {
      filtered = filtered.filter(segment => {
        const statusValue = segment.segment_status ? 'active' : 'inactive';
        return this.selectedStatuses.includes(statusValue);
      });
    }

    // Apply search filter
    if (this.searchTerm && this.searchTerm.trim() !== '') {
      const searchLower = this.searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        segment =>
          segment.name.toLowerCase().includes(searchLower) ||
          segment.description.toLowerCase().includes(searchLower)
      );
    }

    this.filteredSegments = filtered;
    this.cdr.detectChanges();
  }

  // Getters for filter states
  get hasStatusFilterValues(): boolean {
    return this.selectedStatuses.length > 0;
  }

  get hasDraftStatusFilterValues(): boolean {
    return this.draftSelectedStatuses.length > 0;
  }

  /** True when draft selections differ from the applied filter (including clearing to empty). */
  get hasDraftStatusFilterChanges(): boolean {
    const draft = [...this.draftSelectedStatuses].sort().join(',');
    const applied = [...this.selectedStatuses].sort().join(',');
    return draft !== applied;
  }

  // Check if there are active filters or search
  get hasActiveFiltersOrSearch(): boolean {
    return this.hasStatusFilterValues || this.searchTerm.length > 0;
  }

  // Get empty state configuration based on whether filters are active
  /**
   * Determine the empty state configuration for the segments table based on original and filtered segment counts.
   * @example
   * emptyStateConfig()
   * { title: 'No segments created!', description: 'Create your first segment to organize and target your customers.', showAddButton: true }
   * @param {Object} this - Component instance; uses this.originalSegmentCount (number) and this.filteredSegments (array) to decide the empty state.
   * @returns {Object} The empty state configuration object containing title, description, and showAddButton.
   */
  get emptyStateConfig() {
    const config = (() => {
      // If no segments exist at all (original count is 0), show add button
      if (this.originalSegmentCount === 0) {
        return {
          title: 'No segments created!',
          description: 'Create your first segment to organize and target your customers.',
          showAddButton: true,
        };
      }
      // If segments exist originally but current view is empty (due to filters/search), don't show add button
      else if (this.originalSegmentCount > 0 && this.filteredSegments.length === 0) {
        return {
          title: 'No results found',
          description:
            "Try adjusting your search terms or filters to find what you're looking for.",
          showAddButton: false,
        };
      }
      return {
        title: 'No segments found',
        description: '',
        showAddButton: false,
      };
    })();

    return config;
  }

  // Sort methods
  getSortIcon(field: string): string {
    return CampaignSortUtils.getSortIcon(field, this.currentSortField, this.currentSortOrder);
  }

  customSort(event: any): void {
    CampaignSortUtils.customSort(event, this, this.cdr);
  }

  // Get status display name
  getStatusDisplay(status: boolean): string {
    return status ? 'Active' : 'Inactive';
  }

  /** Upload vs rule segment source label for the Type column. */
  getSourceTypeDisplay(sourceType?: string): string {
    return sourceType === 'upload' ? 'Segment_Upload' : 'Segment_Rule';
  }

  // Pagination methods
  onPageChange(event: any): void {
    this.currentPage = event.page;
    this.rowsPerPage = event.rows;
    this.pageChange.emit({ page: event.page, rows: event.rows });
  }

  onRowsPerPageChange(event: any): void {
    this.rowsPerPage = event.value;
    this.currentPage = 0; // Reset to first page when changing rows per page
    this.rowsPerPageChange.emit({ value: event.value });
  }

  get totalPages(): number {
    const total = Number(this.totalRecords) || 0;
    return Math.max(1, Math.ceil(total / this.rowsPerPage));
  }

  get isFirstPage(): boolean {
    return this.currentPage <= 0;
  }

  get isLastPage(): boolean {
    return this.currentPage >= this.totalPages - 1;
  }

  goToPage(page: number): void {
    const target = Math.min(Math.max(page, 0), this.totalPages - 1);
    if (target === this.currentPage) {
      return;
    }
    this.onPageChange({ page: target, rows: this.rowsPerPage });
  }

  getEndItem(): number {
    return Math.min((this.currentPage + 1) * this.rowsPerPage, this.totalRecords);
  }

  onAddSegment(): void {
    this.addSegment.emit();
  }

  // Format created date in "month date year" format (e.g., "Jan 15 2024")
  formatCreatedDate(dateString: string | null | undefined): string {
    return formatDateMonthDayYear(dateString);
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
